"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/app/_module/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

// ── Touch support (native shell) ──
// Radix tooltips are hover-only by design, so every ⓘ icon was dead on the
// iOS app. Inside the mobile shell (body.mobile-app) each Tooltip becomes
// controlled: TAP the trigger to toggle, tap anywhere else (or wait 5s) to
// dismiss. Desktop keeps the untouched hover behavior.
const MobileTipContext = React.createContext<{
  open: boolean;
  setOpen: (v: boolean) => void;
} | null>(null);

const useIsMobileShell = () => {
  const [isShell, setIsShell] = React.useState(false);
  React.useEffect(() => {
    setIsShell(document.body.classList.contains("mobile-app"));
  }, []);
  return isShell;
};

const Tooltip = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const isShell = useIsMobileShell();
  const [open, setOpen] = React.useState(false);

  // Auto-dismiss after 5s so a stray tooltip never sticks around
  React.useEffect(() => {
    if (!isShell || !open) return;
    const t = setTimeout(() => setOpen(false), 5000);
    return () => clearTimeout(t);
  }, [isShell, open]);

  // Tap-outside dismiss
  React.useEffect(() => {
    if (!isShell || !open) return;
    const close = () => setOpen(false);
    // defer so the opening tap itself doesn't immediately close it
    const id = setTimeout(() => document.addEventListener("pointerdown", close, { once: true }), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("pointerdown", close);
    };
  }, [isShell, open]);

  if (!isShell) {
    return <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>;
  }
  return (
    <MobileTipContext.Provider value={{ open, setOpen }}>
      {/* key remount avoids Radix's controlled/uncontrolled flip warning */}
      <TooltipPrimitive.Root key="touch" open={open} onOpenChange={setOpen} delayDuration={0} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </MobileTipContext.Provider>
  );
};

const TooltipTrigger = ({
  onClick,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>) => {
  const mobile = React.useContext(MobileTipContext);
  return (
    <TooltipPrimitive.Trigger
      {...props}
      onClick={(e) => {
        if (mobile) {
          e.preventDefault();
          e.stopPropagation();
          mobile.setOpen(!mobile.open);
        }
        onClick?.(e);
      }}
    />
  );
};

// Remove forwardRef to avoid compose-refs infinite loop bug during rapid re-renders
// Wrapping is forced on here: the content is not portalled, so it inherits
// whitespace rules from its container and a nowrap ancestor would clip the copy.
const TooltipContent = ({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>) => (
  <TooltipPrimitive.Content
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden whitespace-normal break-words rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
);
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
